import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import subscriptionApi, { SubscriptionDetails, SubscriptionStatus, SUBSCRIPTION_STATUS } from '../services/subscriptionApi';

interface SubscriptionContextValue {
  // Current workspace subscription
  subscription: SubscriptionDetails | null;
  loading: boolean;
  error: string | null;
  
  // Active workspace ID
  activeWorkspaceId: number | null;
  
  // Actions
  setActiveWorkspace: (workspaceId: number | null) => void;
  refreshSubscription: () => Promise<void>;
  clearSubscription: () => void;
  
  // Computed helpers
  isTrialExpiringSoon: boolean;  // <= 2 days
  needsPaymentMethod: boolean;
  canConvert: boolean;
  canCancel: boolean;
  canReactivate: boolean;
  canRedeemLifetimeDeal: boolean;
  
  // Banner state
  dismissedBanners: Record<string, boolean>;
  dismissBanner: (bannerId: string) => void;
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined);

const BANNER_DISMISS_KEY = 'dfox_dismissed_banners';
const BANNER_DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours

interface DismissedBanner {
  dismissedAt: number;
  workspaceId: number;
}

function loadDismissedBanners(): Record<string, DismissedBanner> {
  try {
    const stored = localStorage.getItem(BANNER_DISMISS_KEY);
    if (!stored) return {};
    const banners = JSON.parse(stored);
    const now = Date.now();
    // Filter out expired dismissals
    const valid: Record<string, DismissedBanner> = {};
    for (const [key, value] of Object.entries(banners)) {
      const banner = value as DismissedBanner;
      if (now - banner.dismissedAt < BANNER_DISMISS_DURATION) {
        valid[key] = banner;
      }
    }
    return valid;
  } catch {
    return {};
  }
}

function saveDismissedBanner(bannerId: string, workspaceId: number) {
  const banners = loadDismissedBanners();
  banners[bannerId] = { dismissedAt: Date.now(), workspaceId };
  localStorage.setItem(BANNER_DISMISS_KEY, JSON.stringify(banners));
}

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<number | null>(null);
  const [dismissedBannersRaw, setDismissedBannersRaw] = useState<Record<string, DismissedBanner>>(() => loadDismissedBanners());

  const refreshSubscription = useCallback(async () => {
    if (!activeWorkspaceId) {
      setSubscription(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await subscriptionApi.getSubscription(activeWorkspaceId);
      setSubscription(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription');
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  const setActiveWorkspace = useCallback((workspaceId: number | null) => {
    setActiveWorkspaceId(workspaceId);
  }, []);

  const clearSubscription = useCallback(() => {
    setSubscription(null);
    setActiveWorkspaceId(null);
    setError(null);
  }, []);

  const dismissBanner = useCallback((bannerId: string) => {
    if (!activeWorkspaceId) return;
    saveDismissedBanner(bannerId, activeWorkspaceId);
    setDismissedBannersRaw(loadDismissedBanners());
  }, [activeWorkspaceId]);

  // Load subscription when workspace changes
  useEffect(() => {
    if (activeWorkspaceId) {
      refreshSubscription();
    }
  }, [activeWorkspaceId, refreshSubscription]);

  // Computed values
  const status = subscription?.status as SubscriptionStatus | undefined;
  
const isTrialExpiringSoon = Boolean(
    status === SUBSCRIPTION_STATUS.TRIAL &&
    subscription?.trial?.days_remaining !== null &&
    subscription?.trial?.days_remaining !== undefined &&
    subscription.trial.days_remaining <= 2
  );

  const needsPaymentMethod = Boolean(
    subscription &&
    !subscription.has_payment_method &&
[SUBSCRIPTION_STATUS.TRIAL, SUBSCRIPTION_STATUS.TRIAL_EXPIRED, SUBSCRIPTION_STATUS.TRANSFER_TRIAL].includes(status as any)
  );

const canConvert = Boolean(
    [SUBSCRIPTION_STATUS.TRIAL, SUBSCRIPTION_STATUS.TRANSFER_TRIAL].includes(status as any) &&
    subscription?.has_payment_method
  );

  const canCancel = Boolean(
    status &&
    [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL, SUBSCRIPTION_STATUS.PAST_DUE].includes(status as any)
  );

  const canReactivate = Boolean(
    status === SUBSCRIPTION_STATUS.CANCELLED
  );

  const canRedeemLifetimeDeal = Boolean(
    status &&
    subscriptionApi.canRedeemLifetimeDeal(status)
  );

  // Build dismissed banners map for current workspace
  const dismissedBanners: Record<string, boolean> = {};
  if (activeWorkspaceId) {
    for (const [key, value] of Object.entries(dismissedBannersRaw)) {
      if (value.workspaceId === activeWorkspaceId) {
        dismissedBanners[key] = true;
      }
    }
  }

  const value: SubscriptionContextValue = {
    subscription,
    loading,
    error,
    activeWorkspaceId,
    setActiveWorkspace,
    refreshSubscription,
    clearSubscription,
    isTrialExpiringSoon,
    needsPaymentMethod,
    canConvert,
    canCancel,
    canReactivate,
    canRedeemLifetimeDeal,
    dismissedBanners,
    dismissBanner,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
